import { AuthUser } from "aws-amplify/auth";
import L, { map } from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import i18n from "i18next";
import I18nTextUtils from "../../utils/i18n/I18nTextUtils";
import PlaceDataUtils, { PlaceDefinition } from "../../utils/PlaceDataUtils";
import OneStarRatingThumbnail from '../rating/OneStarRatingThumbnail';
import PlaceCategoryBadgeNoAmplify from '../places/PlaceCategoryBadgeNoAmplify';
import PlacePropertyBadgeNoAmplify from '../places/PlacePropertyBadgeNoAmplify';
import { PlaceMarker } from './PlaceMarker';
import './PlaceMarkerNoHover.css';
import StorageUtils from "../../utils/StorageUtils";
import EmptyImageUtils from "../../utils/image/EmptyImageUtils";

export class PlaceMarkerNoHoverClass extends PlaceMarker {
    private onExpandedChange: (isExpanded: boolean) => void;
    private isExpanded: boolean = false;
    private mainImageUrl: string | null = null;
    private sharedMap: L.Map;
    // Used to adapt style with fadeIn animation for initial marker creation only
    private isInitiated: boolean = false;
    // Store event handler reference for cleanup
    private markerExpandHandler!: (e: CustomEvent) => void;

    constructor(
        placeDefinition: PlaceDefinition,
        loggedInUser: AuthUser,
        handlePlaceClick: (placeDefinition: PlaceDefinition) => void,
        onExpandedChange: (isExpanded: boolean) => void,
        map: L.Map
    ) {
        super(placeDefinition, loggedInUser, handlePlaceClick);
        this.onExpandedChange = onExpandedChange;
        this.sharedMap = map;
        this.setIcon(super.createInvisibleIcon());
        this.setupClickHandler();
        this.setupOutsideClickHandler();
        this.setupExpandListener();
        this.loadMainImage();
    }

    private async loadMainImage() {
        try {
            const mainImagePath = await StorageUtils.getPlaceMainImagePath(this.placeDefinition.placeId);
            if (mainImagePath) {
                const url = await StorageUtils.getImageUrl(mainImagePath);
                this.mainImageUrl = url;
                this.setIcon(this.createCustomIcon()); // Update icon with new image
            } else {
                this.mainImageUrl = EmptyImageUtils.getPlaceholderImage();
                this.setIcon(this.createCustomIcon()); // Update icon with placeholder
            }
        } catch (error) {
            console.error("Failed to load main image:", error);
            this.mainImageUrl = EmptyImageUtils.getPlaceholderImage();
            this.setIcon(this.createCustomIcon()); // Update icon with placeholder
        }
    }

    public setExpanded(expanded: boolean) {
        this.isExpanded = expanded;
        
        // Get the DOM element and modify it directly instead of recreating
        // Main reason is to allow CSS to dynamically expand Place Marker (like with hover version)
        const element = this.getMarkerElement();
        if (element) {
            const container = element.querySelector('.marker-container-nohover') as HTMLElement;
            if (container) {
                if (expanded) {
                    container.classList.add('expanded');
                    // Alternative: Dispatch custom event for CSS pseudo-class support
                    container.dispatchEvent(new CustomEvent('marker-expanded', { bubbles: true }));
                } else {
                    container.classList.remove('expanded');
                    // Alternative: Dispatch custom event for CSS pseudo-class support
                    container.dispatchEvent(new CustomEvent('marker-collapsed', { bubbles: true }));
                }
            }
        }
        
        this.setZIndexOffset(expanded ? 1000 : 0);
    }

    // Helper method to get the DOM element
    private getMarkerElement(): HTMLElement | undefined {
        return this.getElement();
    }

    private createCustomIcon(): L.DivIcon {
        const categoryIcon = PlaceDataUtils.getPlaceCategoryDetail(this.placeDefinition.placeCategories[0])?.iconPath;
        const averageRating = this.placeDefinition.averageRating || 0;
        const normalizedRating = averageRating / 5;

        const containerClass = this.isInitiated ? "marker-container-nohover" : "marker-container-nohover marker-container-nohover-new";

        // Set initiated to true after first call
        if (!this.isInitiated) {
            this.isInitiated = true;
        }

        const markerHtml = ReactDOMServer.renderToStaticMarkup(
            <div className={containerClass}>
                <div className="marker-place-name-badge-nohover">
                    {I18nTextUtils.resolveInternationalizedText(this.placeDefinition.name)}
                </div>
                <div className="marker-content-flex-nohover">
                    <div className="marker-circle-nohover">
                        <img src={categoryIcon} alt="category" className="marker-category-icon-nohover" />
                        {this.mainImageUrl && (
                            <img
                                src={this.mainImageUrl}
                                className="marker-main-image-nohover"
                            />
                        )}
                    </div>
                    <div className="marker-pointer-nohover"></div>
                </div>
                <div className="marker-right-wing-flex-column-nohover disableclick">
                    <div className="marker-category-badge-nohover">
                        <PlaceCategoryBadgeNoAmplify categoryEnum={this.placeDefinition.placeCategories[0]} />
                    </div>
                    <div className="marker-rating-nohover">
                        <OneStarRatingThumbnail
                            rating={normalizedRating}
                            numberOfRatings={this.placeDefinition.numberOfRatings}
                        />
                    </div>
                </div>
                <div className="marker-left-wing-flex-column-nohover disableclick">
                    {[...(this.placeDefinition.placeProperties || [])]
                        .sort((a, b) => {
                            const aLength = i18n.t('place.properties.' + a).length;
                            const bLength = i18n.t('place.properties.' + b).length;
                            return aLength - bLength;
                        })
                        .map((property) => (
                            <div key={property} className="marker-property-badge-nohover disableclick">
                                <PlacePropertyBadgeNoAmplify propertyEnum={property} />
                            </div>
                        ))}
                </div>
            </div>
        );

        return L.divIcon({
            className: 'custom-marker-nohover',
            html: markerHtml,
            iconSize: [30, 40],
            iconAnchor: [15, 40]
        });
    }

    private setupClickHandler() {
        this.on('click', (e: L.LeafletMouseEvent) => {
            // Stop propagation
            L.DomEvent.stopPropagation(e);
            if (e.originalEvent) {
                e.originalEvent.stopPropagation();
            }

            if (this.isExpanded) {
                this.handlePlaceClick(this.placeDefinition);
            } else {
                this.onExpandedChange(true);
                const event = new CustomEvent('markerExpand', { detail: this.placeDefinition.placeId });
                document.dispatchEvent(event);
                this.setExpanded(true);
            }
        });

        // Add right-click handler for "open in new tab" functionality
        this.on('contextmenu', (e: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(e);
            if (e.originalEvent) {
                e.originalEvent.preventDefault();
                e.originalEvent.stopPropagation();
            }
            // Open place URL in new tab
            const placeUrl = `${window.location.origin}/place/${this.placeDefinition.placeId}`;
            window.open(placeUrl, '_blank');
        });
    }

    // TODO Place view not scrollable when open on no hover screen - test on mobile
    // This worked day after the problem - keep testing to see if the problem persists

    // TODO issue with getting address on touchscreen place creation 
    // is caused by CORS issue - only from touchscreen - one difference are reqeust headers:
    // sec-ch-ua-mobile: ?1, sec-ch-ua-platform: "Android"
    // while 0 and 'windows' are in not touchscreen page.

    private setupOutsideClickHandler() {
        this.sharedMap.on('click', (e) => {
            if (this.isExpanded) {
                // Add custom property to indicate this click should be ignored
                (e as any).skipPlaceOpenDueToPreviouslyExpandedPlace = true;

                // Also stop the Leaflet event
                L.DomEvent.stop(e);

                this.setExpanded(false);
                this.onExpandedChange(false);
            }
        });
    }

    private setupExpandListener() {
        this.markerExpandHandler = (e: CustomEvent) => {
            if (e.detail !== this.placeDefinition.placeId && this.isExpanded) {
                this.setExpanded(false);
                this.onExpandedChange(false);
            }
        };

        document.addEventListener('markerExpand', this.markerExpandHandler as EventListener);
    }

    // Cleanup method to remove event listeners
    public destroy() {
        if (this.markerExpandHandler) {
            document.removeEventListener('markerExpand', this.markerExpandHandler as EventListener);
        }
    }
} 